<template>
    <div class="w-full md:flex justify-between items-center">
        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="sm:col-span-1">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                    {{ $t('messages.sort_order') }}
                </label>
                <div class="mt-1">
                    <label v-for="sortOption in sortOptions" :key="sortOption.value">
                        <input
                            type="radio"
                            v-model="sort"
                            :value="sortOption.value"
                            name="sortOrder"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                        />
                        {{ sortOption.name }}
                    </label>
                </div>
            </div>

            <div class="sm:col-span-1 hidden">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                    {{ $t('messages.filter') }}
                </label>
                <div class="mt-1">
                    <input
                        type="search"
                        v-model="search"
                        tabindex="1"
                        class="bg-indigo-50 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                    />
                </div>
            </div>
        </div>
    </div>

    <form :action="url" method="post" @submit.prevent="submit">
        <div>
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <transition-group class="min-w-full divide-y divide-gray-200" tag="table" name="card">
                            <thead key="thead">
                                <tr>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.student') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.score') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        d10
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.internal_teacher_note') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.adapted') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.absent') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.signed') }}
                                        <span class="font-mono">({{ signedStudentsCount }}/{{ totalStudents }})</span>
                                    </th>
                                </tr>
                            </thead>
                            <ActivityResponseGrade
                                v-for="(student, index) in form.students"
                                :key="student.id"
                                :student="student.info"
                                v-model="student.activity_response"
                                :errors="form.errors"
                                :index="index"
                                :grade-translator="gradeTranslator"
                            />
                        </transition-group>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-end mt-5" v-if="canEdit">
            <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
        </div>
    </form>
    <!--    <pre>{{ form.students }} </pre>-->
</template>

<script>
import GradeTranslator from '@/classes/GradeTranslator.js';
import ActivityResponseGrade from '@/components/pageSpecific/ActivityResponseGrade.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        ActivityResponseGrade,
        SubmitFormButton,
    },
    props: {
        url: {
            type: String,
            required: true,
        },
        students: {
            type: Array,
            required: true,
        },
        ranges: {
            type: Array,
            required: true,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        groupId: {
            type: Number,
            required: true,
        },
    },
    emits: ['decimalScores'],
    data() {
        return {
            search: '',
            sort: 'last_names',
            sortOptions: [
                {
                    name: this.$t('messages.last_names'),
                    value: 'last_names',
                },
                {
                    name: this.$t('messages.first_name'),
                    value: 'first_name',
                },
            ],
            gradeTranslator: new GradeTranslator(this.ranges),
            form: this.$inertia.form({
                students: this.students,
                group_id: this.groupId,
            }),
        };
    },
    methods: {
        submit() {
            this.form.patch(this.url);
        },
        translateToDecimal(score, max_score) {
            if (!score) {
                return '';
            }

            return ((score / max_score) * 10).toFixed(2);
        },
    },
    computed: {
        decimalScores: function () {
            return this.students.map((s) => s.activity_response.decimal_score);
        },
        totalStudents: function () {
            return this.students.length;
        },
        signedStudentsCount: function () {
            return this.form.students.filter((s) => s.activity_response.signed).length;
        },
    },
    watch: {
        decimalScores: {
            immediate: true,
            handler: function (value) {
                this.$emit('decimalScores', value);
            },
        },
        sort: {
            immediate: true,
            handler: function (newSort) {
                if (newSort === 'last_names') {
                    this.form.students.sort((a, b) => a.info.full_name.localeCompare(b.info.full_name));
                } else {
                    this.form.students.sort((a, b) => {
                        const aName = a.info.name_or_nickname + a.full_name;
                        const bName = b.info.name_or_nickname + b.full_name;
                        return aName.localeCompare(bName);
                    });
                }
            },
        },
    },
};
</script>
<style>
.card-move {
    transition: transform 0.5s;
}
</style>
